// Lets an org admin opt in to AI on uploads, automatic or manual (ENG-3664).
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Modal, message } from 'antd';
import { CursorClick, Lightning, Sparkle } from '@phosphor-icons/react';

import { enableFeature } from '../../actions/userProfile';
import { setManualAiOnUploads } from '../../actions/auth';
import { captureAnalytics } from '../../actions/preload';
import { dismissAiOnUploadsPrompt } from '../../util/aiOnUploadsPrompt';
import './aiOnUploadsPrompt.scss';

const SUCCESS_MESSAGES = {
  auto_ai_on_uploads: 'AI will now run on new uploads.',
  manual_ai_on_uploads: 'Run AI is now available on the Uploads page.',
};

export class AiOnUploadsPrompt extends Component {
  constructor(props) {
    super(props);
    this.state = { saving: null };
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  // The X, the mask and "Not now" all dismiss for this admin in this browser only.
  dismiss = () => {
    if (this.state.saving) return;
    captureAnalytics('ai_on_uploads_prompt_dismissed');
    dismissAiOnUploadsPrompt(this.props.modalType?.data?.uid);
    this.props.hideModal();
  };

  choose = async (feature) => {
    if (this.state.saving) return;
    this.setState({ saving: feature });
    captureAnalytics('ai_on_uploads_prompt_chosen', { feature });
    const result = await this.props.enableFeature(feature);
    if (result?.error) {
      if (!this.unmounted) this.setState({ saving: null });
      message.error(result.response?.data?.error || 'Could not turn this on. Please try again.');
      return;
    }
    if (feature === 'manual_ai_on_uploads') this.props.setManualAiOnUploads(true);
    message.success(SUCCESS_MESSAGES[feature]);
    // Another dialog (e.g. the organization selector) may have replaced this
    // one mid-save; hiding now would close that one instead.
    if (!this.unmounted) this.props.hideModal();
  };

  render() {
    const { saving } = this.state;
    // Home passes this from billing_v2_routed; unknown means no manual choice.
    const manualAvailable = this.props.modalType?.data?.manualAvailable === true;
    // Passed as the antd title (not body content) so antd labels the dialog with it.
    const title = (
      <>
        <div className="ai-uploads-prompt-icon"><Sparkle size={24} weight="fill" /></div>
        <h2 className="ai-uploads-prompt-title">Let AI fill in your uploads</h2>
      </>
    );

    return (
      <Modal
        visible
        width={520}
        footer={null}
        onCancel={this.dismiss}
        // While a choice is saving, nothing may close the dialog: a late
        // hideModal would otherwise close whatever modal opened next.
        closable={!saving}
        maskClosable={!saving}
        keyboard={!saving}
        wrapClassName="ai-uploads-prompt"
        title={title}
      >
        <p className="ai-uploads-prompt-text">
          Medsender can read each document you upload and fill in the patient, title and category.
          Pages AI reads count toward your plan&apos;s transmission pages, just like fax pages.
        </p>
        <button
          type="button"
          className="ai-uploads-prompt-choice ai-uploads-prompt-choice--recommended"
          disabled={Boolean(saving)}
          onClick={() => this.choose('auto_ai_on_uploads')}
        >
          <Lightning size={20} weight="fill" />
          <span>
            <strong>
              Run automatically on new uploads
              <em>Recommended</em>
            </strong>
            <small>
              No clicks needed for files uploaded in Medsender (Printer Driver uploads aren&apos;t included yet).
              Each page counts as ½ a transmission page, so a 10-page upload uses 5.
            </small>
          </span>
        </button>
        {manualAvailable && (
          <button
            type="button"
            className="ai-uploads-prompt-choice"
            disabled={Boolean(saving)}
            onClick={() => this.choose('manual_ai_on_uploads')}
          >
            <CursorClick size={20} weight="fill" />
            <span>
              <strong>Run manually when I choose</strong>
              <small>Select uploads and click Run AI. Each page counts as 1 transmission page, so a 10-page upload uses 10.</small>
            </span>
          </button>
        )}
        <div className="ai-uploads-prompt-footer">
          <span>You can change this later in Settings → Features.</span>
          <button type="button" className="ai-uploads-prompt-dismiss" disabled={Boolean(saving)} onClick={this.dismiss}>Not now</button>
        </div>
      </Modal>
    );
  }
}

AiOnUploadsPrompt.propTypes = {
  hideModal: PropTypes.func.isRequired,
  enableFeature: PropTypes.func.isRequired,
  setManualAiOnUploads: PropTypes.func.isRequired,
  modalType: PropTypes.shape({ data: PropTypes.shape({ manualAvailable: PropTypes.bool, uid: PropTypes.string }) }),
};

AiOnUploadsPrompt.defaultProps = {
  modalType: {},
};

export default connect(null, { enableFeature, setManualAiOnUploads })(AiOnUploadsPrompt);
